import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Building2, Mail, Phone, MapPin, Globe } from 'lucide-react';

const MentionsLegales = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Mentions Légales
        </h1>

        {/* Éditeur */}
        <Card className="border-0 shadow-sm mb-8">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#E53935]/10 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#E53935]" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Éditeur du site</h2>
            </div>
            
            <div className="space-y-4 text-slate-600">
              <p className="text-lg font-semibold text-slate-900">
                PASSERELLE NUMÉRIQUE INC.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#E53935] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-700">Adresse</p>
                    <p>1-645 rue Georges-Bizet</p>
                    <p>Montréal (Québec) H1L5S8</p>
                    <p>Canada</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-[#E53935] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-700">Numéro d&apos;entreprise (NEQ)</p>
                    <p className="font-mono text-lg">1182138157</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="border-0 shadow-sm mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Contact</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#E53935]" />
                <div>
                  <p className="font-medium text-slate-700">Email</p>
                  <a href="mailto:contact@dockhta.com" className="text-[#1976D2] hover:underline">
                    contact@dockhta.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#E53935]" />
                <div>
                  <p className="font-medium text-slate-700">Téléphone</p>
                  <a href="tel:+237621055365" className="text-[#1976D2] hover:underline">
                    +237 621 05 53 65
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hébergement */}
        <Card className="border-0 shadow-sm mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Hébergement</h2>
            
            <div className="text-slate-600 space-y-2">
              <p>Le site DOCKHTA est hébergé sur des infrastructures cloud sécurisées.</p>
              <p>Les données sont stockées et traitées conformément aux réglementations applicables en matière de protection des données personnelles.</p>
            </div>
          </CardContent>
        </Card>

        {/* Propriété intellectuelle */}
        <Card className="border-0 shadow-sm mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Propriété intellectuelle</h2>
            
            <div className="text-slate-600 space-y-4">
              <p>
                L&apos;ensemble des contenus présents sur le site DOCKHTA (textes, images, logos, graphismes, icônes, logiciels, bases de données, etc.) sont la propriété exclusive de PASSERELLE NUMÉRIQUE INC. ou de ses partenaires et sont protégés par les lois relatives à la propriété intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite sauf autorisation écrite préalable de PASSERELLE NUMÉRIQUE INC.
              </p>
              <p>
                La marque DOCKHTA et son logo sont des marques déposées de PASSERELLE NUMÉRIQUE INC. Toute utilisation non autorisée de ces éléments engage la responsabilité de son auteur.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Données personnelles */}
        <Card className="border-0 shadow-sm mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Protection des données personnelles</h2>
            
            <div className="text-slate-600 space-y-4">
              <p>
                DOCKHTA collecte et traite des données personnelles dans le cadre de l&apos;utilisation de ses services. Ces données sont traitées conformément à notre{' '}
                <Link to="/privacy" className="text-[#1976D2] hover:underline">
                  Politique de confidentialité
                </Link>.
              </p>
              <p>
                Conformément à la réglementation applicable, vous disposez d&apos;un droit d&apos;accès, de rectification, de suppression et de portabilité de vos données personnelles. Pour exercer ces droits, vous pouvez nous contacter à l&apos;adresse : contact@dockhta.com
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Responsabilité */}
        <Card className="border-0 shadow-sm mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Limitation de responsabilité</h2>
            
            <div className="text-slate-600 space-y-4">
              <p>
                DOCKHTA est une plateforme de mise en relation entre patients et professionnels de santé. Elle ne se substitue en aucun cas à une consultation médicale et ne pose pas de diagnostic.
              </p>
              <p>
                Les informations fournies par l&apos;assistant IA santé sont à titre informatif uniquement et ne constituent pas un avis médical. En cas de symptômes graves ou d&apos;urgence, veuillez consulter un professionnel de santé ou contacter les services d&apos;urgence.
              </p>
              <p>
                PASSERELLE NUMÉRIQUE INC. ne pourra être tenue responsable des dommages directs ou indirects résultant de l&apos;utilisation des services DOCKHTA.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Droit applicable */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Droit applicable</h2>
            
            <div className="text-slate-600 space-y-4">
              <p>
                Les présentes mentions légales sont régies par le droit canadien. En cas de litige, les tribunaux de Montréal (Québec) seront seuls compétents.
              </p>
              <p className="text-sm text-slate-500">
                Dernière mise à jour : Juin 2026
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer simple */}
      <footer className="bg-[#0F172A] text-white py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2026 DOCKHTA - PASSERELLE NUMÉRIQUE INC. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MentionsLegales;
